import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  IconButton,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  Switch,
  FormHelperText
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Add as AddIcon,
  PhoneForwarded,
  TransferWithinAStation,
  Dialpad,
  DataObject,
  PhoneDisabled
} from '@mui/icons-material';
import { agentService } from '../services/api';

const ToolConfigurationModal = ({ 
  open, 
  onClose, 
  toolType, 
  initialConfig = {}, 
  onSave 
}) => {
  const [config, setConfig] = useState({});
  const [variables, setVariables] = useState([]);
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Fetch available agents for agent_swap tool
  const fetchAvailableAgents = useCallback(async () => {
    if (toolType === 'agent_swap') {
      setLoadingAgents(true);
      try {
        const agents = await agentService.getAgents();
        setAvailableAgents(agents);
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAvailableAgents([]);
      } finally {
        setLoadingAgents(false);
      }
    }
  }, [toolType]);

  useEffect(() => {
    if (open) {
      // Fetch agents if needed
      fetchAvailableAgents();
      
      // Initialize with default values based on capability type
      let defaultConfig = { ...initialConfig };
      
      // Determine the actual capability type from config or toolType
      const capabilityType = initialConfig.capabilityType || toolType;
      
      if (capabilityType === 'transfer_call') {
        defaultConfig = {
          name: initialConfig.name || 'transfer_call',
          description: initialConfig.description || 'Transfer the call to a human agent',
          capabilityType: capabilityType, // Preserve capabilityType
          transfer_destination: initialConfig.transfer_destination || {
            type: 'predefined',
            number: ''
          },
          transfer_option: initialConfig.transfer_option || {
            type: 'cold_transfer',
            show_transferee_as_caller: false
          },
          custom_sip_headers: initialConfig.custom_sip_headers || {}
        };
      } else if (capabilityType === 'agent_swap') {
        defaultConfig = {
          name: initialConfig.name || 'agent_transfer',
          description: initialConfig.description || 'Transfer to another agent',
          capabilityType: capabilityType, // Preserve capabilityType
          target_agent_id: initialConfig.target_agent_id || '',
          execution_message_description: initialConfig.execution_message_description || '',
          post_call_analysis_setting: initialConfig.post_call_analysis_setting || 'both_agents',
          speak_during_execution: initialConfig.speak_during_execution !== undefined ? initialConfig.speak_during_execution : false
        };
      } else if (capabilityType === 'press_digit') {
        defaultConfig = {
          name: initialConfig.name || 'press_digit',
          description: initialConfig.description || 'Press DTMF digits',
          capabilityType: capabilityType, // Preserve capabilityType
          delay_ms: initialConfig.delay_ms || 1000
        };
      } else if (capabilityType === 'extract_dynamic_variable') {
        defaultConfig = {
          name: initialConfig.name || 'extract_variable',
          description: initialConfig.description || 'Extract and store information from the conversation',
          capabilityType: capabilityType, // Preserve capabilityType
          variables: initialConfig.variables && initialConfig.variables.length > 0 
            ? initialConfig.variables 
            : [
                {
                  type: 'string',
                  name: 'variable',
                  description: 'Extracted variable'
                }
              ]
        };
      } else if (capabilityType === 'end_call') {
        defaultConfig = {
          name: initialConfig.name || 'end_call',
          description: initialConfig.description || 'End the current call',
          capabilityType: capabilityType // Preserve capabilityType
        };
      }
      
      setConfig(defaultConfig);
      setVariables(defaultConfig.variables || []);
    }
  }, [open, initialConfig, toolType, fetchAvailableAgents]);

  const handleConfigChange = (field, value) => {
    setConfig(prev => {
      const newConfig = {
        ...prev,
        [field]: value
      };
      
      // Always preserve the capabilityType - never allow it to be changed
      if (initialConfig.capabilityType) {
        newConfig.capabilityType = initialConfig.capabilityType;
      } else if (toolType) {
        newConfig.capabilityType = toolType;
      }
      
      return newConfig;
    });
  };

  // Check if the current configuration is valid
  const isConfigurationValid = () => {
    const capabilityType = config.capabilityType || toolType;
    
    if (capabilityType === 'transfer_call') {
      if (config.transfer_destination?.type === 'predefined') {
        return config.transfer_destination.number && 
               config.transfer_destination.number.trim() !== '' && 
               isValidPhoneNumber(config.transfer_destination.number);
      } else if (config.transfer_destination?.type === 'inferred') {
        return config.transfer_destination.prompt && 
               config.transfer_destination.prompt.trim() !== '';
      }
      return false; // No transfer destination type set
    }
    
    return true; // Other tool types are always valid
  };

  const handleSave = () => {
    // Validate transfer call configuration before saving
    if (toolType === 'transfer_call' || config.capabilityType === 'transfer_call') {
      if (config.transfer_destination?.type === 'predefined') {
        if (!config.transfer_destination.number || config.transfer_destination.number.trim() === '') {
          alert('Please enter a phone number for the transfer destination.');
          return;
        }
        if (!isValidPhoneNumber(config.transfer_destination.number)) {
          alert('Please enter a valid phone number in E.164 format (e.g., +61424908366) or SIP URI.');
          return;
        }
      } else if (config.transfer_destination?.type === 'inferred') {
        if (!config.transfer_destination.prompt || config.transfer_destination.prompt.trim() === '') {
          alert('Please enter a transfer logic prompt for dynamic routing.');
          return;
        }
      }
    }
    
    const finalConfig = {
      ...config,
      variables: variables,
      // Always preserve the capabilityType - this is the source of truth
      capabilityType: initialConfig.capabilityType || toolType
    };
    
    // Double-check: if somehow capabilityType is missing, use toolType as fallback
    if (!finalConfig.capabilityType) {
      finalConfig.capabilityType = toolType;
    }
    
    onSave(toolType, finalConfig);
    onClose();
  };

  const handleAddVariable = () => {
    setEditingVariable(null);
    setShowVariableModal(true);
  };

  const handleEditVariable = (index) => {
    setEditingVariable({ index, variable: variables[index] });
    setShowVariableModal(true);
  };

  const handleDeleteVariable = (index) => {
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveVariable = (variable) => {
    if (editingVariable !== null) {
      // Editing existing variable
      setVariables(prev => prev.map((v, i) => i === editingVariable.index ? variable : v));
    } else {
      // Adding new variable
      setVariables(prev => [...prev, variable]);
    }
    setShowVariableModal(false);
    setEditingVariable(null);
  };

  // Phone number validation function
  const isValidPhoneNumber = (number) => {
    if (!number) return false; // Require a number for transfer calls
    
    // E.164 format: +[country code][number] (total 7-15 digits including country code)
    const e164Regex = /^\+[1-9]\d{1,14}$/;
    
    // SIP URI format: sip:user@domain
    const sipRegex = /^sip:.+@.+/;
    
    return e164Regex.test(number) || sipRegex.test(number);
  };

  // Phone number formatting helper
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    // Remove all non-digit characters except +
    let cleaned = number.replace(/[^\d+]/g, '');
    
    // If it starts with +, keep it
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // If it's a 10-digit number, assume it's US/Canada and add +1
    if (cleaned.length === 10) {
      return '+1' + cleaned;
    }
    
    // If it's an 11-digit number starting with 1, add +
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return '+' + cleaned;
    }
    
    // If it's an 8-digit number, assume it's Australian and add +61
    if (cleaned.length === 8) {
      return '+61' + cleaned;
    }
    
    // If it's a 9-digit number starting with 4, assume it's Australian and add +61
    if (cleaned.length === 9 && cleaned.startsWith('4')) {
      return '+61' + cleaned;
    }
    
    return number; // Return as-is if we can't format it
  };

  const getToolIcon = (type) => {
    switch (type) {
      case 'transfer_call': return <PhoneForwarded />;
      case 'agent_swap': return <TransferWithinAStation />;
      case 'press_digit': return <Dialpad />;
      case 'extract_dynamic_variable': return <DataObject />;
      case 'end_call': return <PhoneDisabled />;
      default: return <DataObject />;
    }
  };

  const getToolTitle = (type) => {
    switch (type) {
      case 'transfer_call': return 'Call Transfer';
      case 'agent_swap': return 'Agent Transfer';
      case 'press_digit': return 'Press Digit IVR Navigation';
      case 'extract_dynamic_variable': return 'Extract Dynamic Variable';
      case 'end_call': return 'End Call';
      default: return 'Tool Configuration';
    }
  };

  const renderTransferCallForm = () => (
    <Box>
      <TextField
        fullWidth
        label="Name"
        value={config.name || 'transfer_call'}
        onChange={(e) => handleConfigChange('name', e.target.value)}
        helperText="Enter a unique name for this capability"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description (Optional)"
        value={config.description || 'Transfer the call to a human agent'}
        onChange={(e) => handleConfigChange('description', e.target.value)}
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" gutterBottom>
        Transfer to:
      </Typography>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          value={config.transfer_destination?.type || 'predefined'}
          onChange={(e) => {
            const newType = e.target.value;
            const newTransferDestination = {
              type: newType
            };
            
            // Clear the other field when switching types
            if (newType === 'predefined') {
              newTransferDestination.number = config.transfer_destination?.number || '';
              // Remove prompt field for predefined type
              delete newTransferDestination.prompt;
            } else if (newType === 'inferred') {
              newTransferDestination.prompt = config.transfer_destination?.prompt || '';
              // Remove number field for inferred type
              delete newTransferDestination.number;
            }
            
            handleConfigChange('transfer_destination', newTransferDestination);
          }}
        >
          <FormControlLabel value="predefined" control={<Radio />} label="Static Destination" />
          <FormControlLabel value="inferred" control={<Radio />} label="Dynamic Routing" />
        </RadioGroup>
      </FormControl>

      {/* Static Destination - Phone Number Field */}
      {config.transfer_destination?.type === 'predefined' && (
        <TextField
          fullWidth
          label="Phone Number"
          value={config.transfer_destination?.number || ''}
          onChange={(e) => {
            const inputValue = e.target.value;
            // Auto-format the phone number if it looks like a regular number
            const formattedValue = inputValue.includes('+') || inputValue.includes('sip:') 
              ? inputValue 
              : formatPhoneNumber(inputValue);
            
            handleConfigChange('transfer_destination', {
              ...config.transfer_destination,
              number: formattedValue
            });
          }}
          onBlur={(e) => {
            // Format on blur if the number is valid but not in E.164 format
            const currentValue = e.target.value;
            if (currentValue && !currentValue.includes('sip:') && !currentValue.startsWith('+')) {
              const formattedValue = formatPhoneNumber(currentValue);
              if (formattedValue !== currentValue) {
                handleConfigChange('transfer_destination', {
                  ...config.transfer_destination,
                  number: formattedValue
                });
              }
            }
          }}
          placeholder="+61424908366"
          helperText={
            config.transfer_destination?.number && !isValidPhoneNumber(config.transfer_destination.number)
              ? "Please enter a valid E.164 format number (e.g., +61424908366) or SIP URI (e.g., sip:user@domain.com)"
              : "Enter a valid phone number in E.164 format (e.g., +61424908366) or SIP URI. Auto-formatting will be applied."
          }
          error={config.transfer_destination?.number && !isValidPhoneNumber(config.transfer_destination.number)}
          sx={{ mb: 3 }}
        />
      )}

      {/* Dynamic Routing - Prompt Field */}
      {config.transfer_destination?.type === 'inferred' && (
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Transfer Logic Prompt"
          value={config.transfer_destination?.prompt || ''}
          onChange={(e) => handleConfigChange('transfer_destination', {
            ...config.transfer_destination,
            prompt: e.target.value
          })}
          placeholder="If the user wants to reach support, transfer to +1 (925) 222-2222; if the user wants to reach sales, transfer to +1 (925) 333-3333"
          helperText="Describe the conditional logic for determining the transfer destination based on the conversation context."
          sx={{ mb: 3 }}
        />
      )}

      <Typography variant="subtitle1" gutterBottom>
        Type:
      </Typography>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          value={config.transfer_option?.type || 'cold_transfer'}
          onChange={(e) => handleConfigChange('transfer_option', {
            ...config.transfer_option,
            type: e.target.value
          })}
        >
          <FormControlLabel 
            value="cold_transfer" 
            control={<Radio />} 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Cold Transfer
                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary' }} />
              </Box>
            } 
          />
          <FormControlLabel 
            value="warm_transfer" 
            control={<Radio />} 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Warm Transfer
                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary' }} />
              </Box>
            } 
          />
        </RadioGroup>
      </FormControl>

      <Typography variant="subtitle1" gutterBottom>
        Displayed Phone Number:
      </Typography>
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          value={config.transfer_option?.show_transferee_as_caller ? 'transferee' : 'agent'}
          onChange={(e) => handleConfigChange('transfer_option', {
            ...config.transfer_option,
            show_transferee_as_caller: e.target.value === 'transferee'
          })}
        >
          <FormControlLabel value="agent" control={<Radio />} label="Agent's Number" />
          <FormControlLabel 
            value="transferee" 
            control={<Radio />} 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                Transferee's Number
                <InfoIcon sx={{ ml: 1, fontSize: 16, color: 'text.secondary' }} />
              </Box>
            } 
          />
        </RadioGroup>
      </FormControl>

      <Typography variant="subtitle1" gutterBottom>
        Custom SIP Headers:
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Add key/value pairs for call routing, metadata, or carrier integration.
      </Typography>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => {/* TODO: Add SIP headers functionality */}}
      >
        + Add
      </Button>
    </Box>
  );

  const renderPressDigitForm = () => (
    <Box>
      <TextField
        fullWidth
        label="Name"
        value={config.name || 'press_digit'}
        onChange={(e) => handleConfigChange('name', e.target.value)}
        helperText="Enter a unique name for this capability"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description (Optional)"
        value={config.description || 'Navigate to the human agent of sales department'}
        onChange={(e) => handleConfigChange('description', e.target.value)}
        sx={{ mb: 3 }}
      />

      <TextField
        fullWidth
        type="number"
        label="Pause Detection Delay (Optional)"
        value={config.delay_ms || 1000}
        onChange={(e) => handleConfigChange('delay_ms', parseInt(e.target.value) || 0)}
        InputProps={{
          endAdornment: <Typography variant="body2">milliseconds</Typography>
        }}
        helperText={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 0.5, fontSize: 16 }} />
            Delay before processing the next action
          </Box>
        }
      />
    </Box>
  );

  const renderExtractVariableForm = () => (
    <Box>
      <TextField
        fullWidth
        label="Function Name"
        value={config.name || ''}
        onChange={(e) => handleConfigChange('name', e.target.value)}
        helperText="Enter a unique name for this capability"
        placeholder="e.g. extract_user_details"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description"
        value={config.description || ''}
        onChange={(e) => handleConfigChange('description', e.target.value)}
        placeholder="e.g. Extract the user's details like name, email, age, etc. from the conversation."
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" gutterBottom>
        Variables:
      </Typography>
      
      {variables.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {variables.map((variable, index) => (
            <Chip
              key={index}
              label={`${variable.name} (${variable.type || 'Text'})`}
              onDelete={() => handleDeleteVariable(index)}
              onClick={() => handleEditVariable(index)}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}
      
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddVariable}
      >
        + Add
      </Button>
    </Box>
  );

  const renderAgentSwapForm = () => (
    <Box>
      <TextField
        fullWidth
        label="Name"
        value={config.name || 'agent_transfer'}
        onChange={(e) => handleConfigChange('name', e.target.value)}
        helperText="Enter a unique name for this capability"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description (Optional)"
        value={config.description || 'Transfer to another agent'}
        onChange={(e) => handleConfigChange('description', e.target.value)}
        sx={{ mb: 3 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Target Agent</InputLabel>
        <Select
          value={config.target_agent_id || ''}
          onChange={(e) => handleConfigChange('target_agent_id', e.target.value)}
          label="Target Agent"
          disabled={loadingAgents}
        >
          <MenuItem value="">
            <em>Select an agent</em>
          </MenuItem>
          {availableAgents.map((agent) => (
            <MenuItem key={agent.id} value={agent.id}>
              {agent.name} {agent.description && `(${agent.description})`}
            </MenuItem>
          ))}
        </Select>
        {loadingAgents && (
          <FormHelperText>Loading agents...</FormHelperText>
        )}
        {!loadingAgents && availableAgents.length === 0 && (
          <FormHelperText>No agents available</FormHelperText>
        )}
      </FormControl>

      <TextField
        fullWidth
        label="Execution Message Description"
        value={config.execution_message_description || ''}
        onChange={(e) => handleConfigChange('execution_message_description', e.target.value)}
        placeholder="Message shown during execution"
        helperText="Optional message to display during the transfer"
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Post Call Analysis Setting</InputLabel>
        <Select
          value={config.post_call_analysis_setting || 'both_agents'}
          onChange={(e) => handleConfigChange('post_call_analysis_setting', e.target.value)}
          label="Post Call Analysis Setting"
        >
          <MenuItem value="both_agents">Both Agents</MenuItem>
          <MenuItem value="only_destination_agent">Only Destination Agent</MenuItem>
        </Select>
        <FormHelperText>
          Choose which agent(s) will receive post-call analysis
        </FormHelperText>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            checked={config.speak_during_execution || false}
            onChange={(e) => handleConfigChange('speak_during_execution', e.target.checked)}
          />
        }
        label="Speak During Execution"
      />
      <FormHelperText>
        Whether the agent should speak during the transfer process
      </FormHelperText>
    </Box>
  );

  const renderEndCallForm = () => (
    <Box>
      <TextField
        fullWidth
        label="Name"
        value={config.name || 'end_call'}
        onChange={(e) => handleConfigChange('name', e.target.value)}
        helperText="Enter a unique name for this capability"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description (Optional)"
        value={config.description || 'End the current call'}
        onChange={(e) => handleConfigChange('description', e.target.value)}
        sx={{ mb: 3 }}
      />
    </Box>
  );



  const renderForm = () => {
    // Determine the actual capability type from config or toolType
    const capabilityType = config.capabilityType || toolType;
    
    switch (capabilityType) {
      case 'transfer_call':
        return renderTransferCallForm();
      case 'press_digit':
        return renderPressDigitForm();
      case 'extract_dynamic_variable':
        return renderExtractVariableForm();
      case 'agent_swap':
        return renderAgentSwapForm();
      case 'end_call':
        return renderEndCallForm();
      default:
        return <Typography>Unknown tool type</Typography>;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 1, color: 'primary.main' }}>
              {getToolIcon(config.capabilityType || toolType)}
            </Box>
            <Typography variant="h6">
              {getToolTitle(config.capabilityType || toolType)}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {renderForm()}
          
          {/* Validation status */}
          {!isConfigurationValid() && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              <Typography variant="body2" color="error.contrastText">
                {(() => {
                  const capabilityType = config.capabilityType || toolType;
                  if (capabilityType === 'transfer_call') {
                    if (config.transfer_destination?.type === 'predefined') {
                      if (!config.transfer_destination.number || config.transfer_destination.number.trim() === '') {
                        return 'Please enter a phone number for the transfer destination.';
                      }
                      if (!isValidPhoneNumber(config.transfer_destination.number)) {
                        return 'Please enter a valid phone number in E.164 format (e.g., +61424908366) or SIP URI.';
                      }
                    } else if (config.transfer_destination?.type === 'inferred') {
                      if (!config.transfer_destination.prompt || config.transfer_destination.prompt.trim() === '') {
                        return 'Please enter a transfer logic prompt for dynamic routing.';
                      }
                    } else {
                      return 'Please select a transfer destination type (Static Destination or Dynamic Routing).';
                    }
                  }
                  return 'Configuration is invalid.';
                })()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!isConfigurationValid()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variable Configuration Modal */}
      <VariableModal
        open={showVariableModal}
        onClose={() => setShowVariableModal(false)}
        variable={editingVariable?.variable}
        onSave={handleSaveVariable}
      />
    </>
  );
};

// Variable Configuration Modal Component
const VariableModal = ({ open, onClose, variable, onSave }) => {
  const [varConfig, setVarConfig] = useState({
    name: '',
    description: '',
    type: 'string'
  });

  useEffect(() => {
    if (open) {
      setVarConfig(variable || {
        name: '',
        description: '',
        type: 'string'
      });
    }
  }, [open, variable]);

  const handleSave = () => {
    onSave(varConfig);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Variables
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <TextField
          fullWidth
          label="Variable Name"
          value={varConfig.name}
          onChange={(e) => setVarConfig(prev => ({ ...prev, name: e.target.value }))}
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Variable Description"
          value={varConfig.description}
          onChange={(e) => setVarConfig(prev => ({ ...prev, description: e.target.value }))}
          sx={{ mb: 2 }}
        />
        
        <FormControl fullWidth>
          <InputLabel>Variable Type (Optional)</InputLabel>
          <Select
            value={varConfig.type}
            onChange={(e) => setVarConfig(prev => ({ ...prev, type: e.target.value }))}
            label="Variable Type (Optional)"
          >
            <MenuItem value="string">Text</MenuItem>
            <MenuItem value="number">Number</MenuItem>
            <MenuItem value="boolean">Boolean</MenuItem>
            <MenuItem value="array">Array</MenuItem>
            <MenuItem value="object">Object</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ToolConfigurationModal; 